import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import { requestPasswordReset } from '../services/authService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
`;

const Card = styled.div`
  max-width: 26rem;
  margin: 0 auto;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 700;
`;

const Sub = styled.p`
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Input = styled.input`
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.9375rem;
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.destructive || '#dc2626'};
  font-size: 0.875rem;
`;

const SuccessText = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.9375rem;
  line-height: 1.6;
`;

const FooterText = styled.p`
  margin: 1.25rem 0 0;
  text-align: center;
  font-size: 0.875rem;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await requestPasswordReset(email.trim());
      setSent(true);
      if (data.message) {
        /* shown via sent state */
      }
    } catch (err) {
      setError(err?.response?.data?.message || t('auth.forgotFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <SEO title={t('auth.forgotTitle')} robots="noindex, follow" />
      <Header />
      <Main>
        <Card>
          <Title>{t('auth.forgotTitle')}</Title>
          <Sub>{sent ? t('auth.forgotSent') : t('auth.forgotSub')}</Sub>
          {!sent ? (
            <form onSubmit={handleSubmit}>
              {error ? <ErrorText>{error}</ErrorText> : null}
              <Field>
                {t('auth.emailLabel')}
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </Field>
              <Button type="submit" disabled={loading} style={{ width: '100%' }}>
                {loading ? t('auth.forgotSending') : t('auth.forgotSubmit')}
              </Button>
            </form>
          ) : (
            <>
              <SuccessText>{t('auth.forgotSent')}</SuccessText>
              <Sub style={{ marginBottom: 0 }}>{t('auth.forgotCheckSpam')}</Sub>
            </>
          )}
          <FooterText>
            <Link to="/login">← {t('auth.signIn')}</Link>
          </FooterText>
        </Card>
      </Main>
      <Footer />
    </PageWrapper>
  );
}
